import React from "react";
import { Match } from "../../types/tournament";
import { TokenAvatar } from "./TokenAvatar";

interface MatchPairingProps {
  match: Match;
  roundType: "grand-finals" | "semi-finals" | "quarter-finals" | "standard";
  className?: string;
}

export const MatchPairing: React.FC<MatchPairingProps> = ({
  match,
  roundType,
  className = "",
}) => {
  if (!match.tokenA || !match.tokenB) return null;

  // Grand Finals - Special large display with original images
  if (roundType === "grand-finals") {
    const hasWinner = match.winner !== undefined;
    const isLive = !hasWinner;

    return (
      <div
        className={`relative bg-gradient-to-r from-purple-900/20 via-black to-cyan-900/20 border-2 border-white/20 rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-2xl ${className}`}
      >
        {isLive && (
          <div className="absolute top-4 right-4 px-3 py-2 bg-red-600 text-white text-sm font-bold rounded animate-pulse">
            LIVE
          </div>
        )}
        <div className="flex items-center justify-center gap-2 sm:gap-8 lg:gap-12">
          <TokenAvatar
            token={match.tokenA}
            size="xxl"
            useOriginalImage={true}
            showLiquidity
            showFullName
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenA}
            isLoser={hasWinner && match.winner !== match.tokenA}
          />
          <div
            className={`text-white text-3xl sm:text-4xl lg:text-5xl font-bold ${
              isLive ? "animate-pulse" : ""
            }`}
          >
            VS
          </div>
          <TokenAvatar
            token={match.tokenB}
            size="xxl"
            useOriginalImage={true}
            showLiquidity
            showFullName
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenB}
            isLoser={hasWinner && match.winner !== match.tokenB}
          />
        </div>
      </div>
    );
  }

  // Semi-Finals - Large display with original images
  if (roundType === "semi-finals") {
    const hasWinner = match.winner !== undefined;

    return (
      <div
        className={`relative bg-gradient-to-r from-purple-900/10 via-black to-cyan-900/10 border-2 border-white/20 rounded-lg p-3 sm:p-4 lg:p-5 ${
          hasWinner ? "border-white/15" : "hover:border-white/30"
        } transition-all shadow-xl ${className}`}
      >
        <div className="flex items-center justify-center gap-2 sm:gap-6 lg:gap-8">
          <TokenAvatar
            token={match.tokenA}
            size="xl"
            useOriginalImage={true}
            showLiquidity
            showFullName
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenA}
            isLoser={hasWinner && match.winner !== match.tokenA}
          />
          <div className="text-white text-xl sm:text-2xl font-bold">VS</div>
          <TokenAvatar
            token={match.tokenB}
            size="xl"
            useOriginalImage={true}
            showLiquidity
            showFullName
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenB}
            isLoser={hasWinner && match.winner !== match.tokenB}
          />
        </div>
      </div>
    );
  }

  // Quarter-Finals - Same as semi-finals
  if (roundType === "quarter-finals") {
    const hasWinner = match.winner !== undefined;

    return (
      <div
        className={`relative bg-black/50 border-2 border-white/30 rounded-md sm:rounded-lg p-2 sm:p-3 ${
          hasWinner ? "border-white/20" : "hover:border-white/40"
        } transition-all ${className}`}
      >
        <div className="flex items-center justify-center gap-1 sm:gap-2">
          <TokenAvatar
            token={match.tokenA}
            size="lg"
            showFullName
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenA}
            isLoser={hasWinner && match.winner !== match.tokenA}
          />
          <div className="mx-0 sm:mx-1">
            <span className="text-white/30 text-xs font-semibold">VS</span>
          </div>
          <TokenAvatar
            token={match.tokenB}
            size="lg"
            showFullName
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenB}
            isLoser={hasWinner && match.winner !== match.tokenB}
          />
        </div>
      </div>
    );
  }

  // Standard rounds - Consistent medium cards
  if (roundType === "standard") {
    const hasWinner = match.winner !== undefined;

    return (
      <div
        className={`relative bg-black/50 border border-white/30 rounded-md p-2 sm:p-3 ${
          hasWinner ? "border-white/20" : "hover:border-white/40"
        } transition-all ${className}`}
      >
        <div className="flex items-center justify-center gap-1">
          <TokenAvatar
            token={match.tokenA}
            size="md"
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenA}
            isLoser={hasWinner && match.winner !== match.tokenA}
          />
          <div className="mx-0 sm:mx-1">
            <span className="text-white/30 text-[10px] sm:text-xs font-semibold">
              V
            </span>
          </div>
          <TokenAvatar
            token={match.tokenB}
            size="md"
            responsiveSize={true}
            isWinner={hasWinner && match.winner === match.tokenB}
            isLoser={hasWinner && match.winner !== match.tokenB}
          />
        </div>
      </div>
    );
  }

  return null;
};
